import { useState } from "react";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { ExternalLink } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { format, startOfMonth, endOfMonth } from "date-fns";
import { fetchPayouts, fetchUserProfile } from "@/utils/apiUtils";
import EarningsDialog from "./EarningsDialog";

const EarningsWidget = () => {
  const [dialogOpen, setDialogOpen] = useState(false);
  
  const startDate = format(startOfMonth(new Date()), "yyyy-MM-dd");
  const endDate = format(endOfMonth(new Date()), "yyyy-MM-dd");
  
  const { data: profileData } = useQuery({
    queryKey: ["userProfile"],
    queryFn: fetchUserProfile,
    staleTime: 300000,
  });
  
  const { data: payoutsData, isLoading } = useQuery({
    queryKey: ["payouts", startDate, endDate],
    queryFn: () => fetchPayouts(startDate, endDate)
  });
  
  const totalEarnings = payoutsData?.results?.reduce((total: number, payout: any) => {
    return total + (parseFloat(payout.amount) || 0);
  }, 0) || 0;
  
  const lifetimeEarnings = profileData?.lifetime_payouts_usd || 0;
  
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };
  
  return (
    <>
      <Card className="shadow-sm bg-white">
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className="text-xl font-bold text-gray-900">My Earnings</CardTitle>
          <button 
            onClick={() => setDialogOpen(true)}
            className="text-gray-400 hover:text-gray-600"
          >
            <ExternalLink size={16} />
          </button>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col space-y-6">
            {isLoading ? (
              <div className="h-40 flex items-center justify-center">
                <p className="text-gray-500">Loading earnings data...</p>
              </div>
            ) : (
              <>
                <div>
                  <p className="text-sm font-medium text-gray-600">This month</p>
                  <h3 className="text-3xl font-bold mt-1 text-gray-900">{formatCurrency(totalEarnings)}</h3>
                </div>
                
                <div className="bg-gray-50 p-4 rounded-lg">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs text-gray-500">Active days</p>
                      <p className="text-lg font-semibold mt-1 text-gray-800">
                        {payoutsData?.results?.length || 0}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Trips completed</p>
                      <p className="text-lg font-semibold mt-1 text-gray-800">
                        {payoutsData?.count || 0}
                      </p>
                    </div>
                  </div>
                </div>
                
                <div className="space-y-1">
                  <div className="h-2 w-full bg-gray-100 rounded-full overflow-hidden">
                    <div className="h-full bg-neon-green rounded-full" style={{ width: `66%` }}></div>
                  </div>
                  <p className="text-xs text-right text-gray-600">
                    {payoutsData?.results?.length || 0} of {endOfMonth(new Date()).getDate()} days
                  </p>
                </div>
              </>
            )}
          </div>
        </CardContent>
      </Card>
      
      <EarningsDialog 
        open={dialogOpen} 
        onOpenChange={setDialogOpen} 
      />
    </>
  );
};

export default EarningsWidget;
